import { action, computed, makeObservable } from 'mobx';

import { redirectUri } from '@/configs/constants';
import { router } from '@/configs/routes';
import { YANDEX_FEDERATION_ID } from '@/configs/secrets';
import { AlertsStore } from '@/configs/store/AlertsStore';
import { UserStore } from '@/configs/store/UserStore';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { ToggleModel } from '@/models/ToggleModel';
import { ValueModel } from '@/models/ValueModel';
import { formatError } from '@/utils/formatError';
import { clearKtsidLoginMark } from '@/utils/ktsidLoginMark';
import { getLegacyLoginReturnPath } from '@/utils/legacyLoginUrl';

const OPEN_IN_NEW_WINDOW_KEY = 'loginPage.openInNewWindow';

export class LoginPageStore {
  private readonly userStore: UserStore;

  private readonly _activeStep = new ValueModel(0);
  private readonly _error = new ValueModel<string | null>(null);
  private readonly _callbackUrl = new ValueModel('');
  private readonly _openInNewWindow: ToggleModel;

  private readonly _alertsStore: AlertsStore;

  private readonly loadingStage = new LoadingStageModel();

  constructor({ userStore, alertsStore }: { userStore: UserStore; alertsStore: AlertsStore }) {
    this.userStore = userStore;
    this._alertsStore = alertsStore;

    const savedValue = localStorage.getItem(OPEN_IN_NEW_WINDOW_KEY);
    this._openInNewWindow = new ToggleModel(savedValue !== null ? savedValue === 'true' : true);
    makeObservable(this, {
      activeStep: computed,
      error: computed,
      callbackUrl: computed,
      openInNewWindow: computed,
      setCallbackUrl: action,
      toggleOpenInNewWindow: action,
      goToPreviousStep: action,
      handleOpenYandexAuth: action,
      handleSubmit: action,
    });
  }

  get activeStep() {
    return this._activeStep.value;
  }

  get callbackUrl() {
    return this._callbackUrl.value;
  }

  get error() {
    return this._error.value;
  }

  get openInNewWindow() {
    return this._openInNewWindow.value;
  }

  get isLoading() {
    return this.loadingStage.isLoading;
  }

  setCallbackUrl = (url: string) => {
    this._callbackUrl.change(url);
  };

  toggleOpenInNewWindow = () => {
    this._openInNewWindow.toggle();
    localStorage.setItem(OPEN_IN_NEW_WINDOW_KEY, String(this._openInNewWindow.value));
  };

  goToPreviousStep = () => {
    if (this._activeStep.value > 0) {
      this._activeStep.change(this._activeStep.value - 1);
    }
  };

  handleOpenYandexAuth = () => {
    const authUrl = `https://console.cloud.yandex.ru/federations/${YANDEX_FEDERATION_ID}?redirectUrl=${encodeURIComponent(redirectUri)}`;

    if (this.openInNewWindow) {
      const popup = window.open(
        authUrl,
        'yandex-auth',
        'width=800,height=600,scrollbars=yes,resizable=yes',
      );

      if (popup) {
        popup.focus();
      }
    } else {
      window.open(authUrl, '_blank');
    }

    this._activeStep.change(1);
  };

  handleSubmit = async () => {
    if (!this.callbackUrl) {
      this._error.change('Пожалуйста, вставьте URL');

      return;
    }

    this._activeStep.change(2);
    this._error.change(null);

    try {
      this.loadingStage.loading();
      const url = new URL(this.callbackUrl);
      const params = new URLSearchParams(url.search);
      const meToken = params.get('token');

      if (!meToken) {
        this.loadingStage.error();
        this._activeStep.change(1);

        this._alertsStore.addAlert({
          message: 'Не удалось получить токен',
          title: 'Ошибка',
          withReload: false,
          autoClose: true,
        });

        return;
      }

      await this.userStore.login(meToken);
      this.loadingStage.success();
      // Вошли токеном ради старого учёта: отметка от брошенного входа через KTSID не должна увести в новый
      clearKtsidLoginMark();
      router.navigate(getLegacyLoginReturnPath(window.location.search));
    } catch (err) {
      this.loadingStage.error();
      this._activeStep.change(1);
      this._error.change(err instanceof Error ? err.message : 'Ошибка при авторизации');

      const error = formatError(err);

      this._alertsStore.addAlert({
        message: error.message,
        title: error.title,
        withReload: false,
        autoClose: true,
      });
    }
  };
}
